import React from "react";
import { Box, Paper, Skeleton, Stack, useMediaQuery, useTheme } from "@mui/material";
import PageContainer from "../PageContainer";
import { MANAGEMENT_AVATAR_SIZE } from "../../styles/managementShared";
import {
  PLANNER_LIST_COLUMNS,
  SENT_EVENT_COLUMNS,
  UPCOMING_EVENT_COLUMNS,
} from "./plannerStyles";

/** Planner pages use the wide layout, like the real pages. */
const PAGE_SX = { maxWidth: 1600 };

const ROWS = [0, 1, 2, 3];

const CARD_SX = { border: "1px solid", borderColor: "divider", borderRadius: 2 } as const;

const useIsMobile = () => {
  const theme = useTheme();
  return !useMediaQuery(theme.breakpoints.up("md"));
};

/** Two stacked lines, e.g. a title and the grey text under it. */
const TwoLines: React.FC<{ first?: string | number; second?: string | number }> = ({
  first = "70%",
  second = "45%",
}) => (
  <Box sx={{ minWidth: 0, flex: 1 }}>
    <Skeleton variant="text" width={first} />
    <Skeleton variant="text" width={second} sx={{ fontSize: "0.75rem" }} />
  </Box>
);

/** A small icon then a line of text, as in an Event's Date, Time and Location columns. */
const IconLine: React.FC<{ width?: string | number }> = ({ width = "70%" }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
    <Skeleton variant="circular" width={18} height={18} sx={{ flexShrink: 0 }} />
    <Skeleton variant="text" width={width} />
  </Box>
);

const Chip: React.FC<{ width?: number }> = ({ width = 64 }) => (
  <Skeleton variant="rounded" width={width} height={22} sx={{ borderRadius: 4 }} />
);

/** The grey column headings above a list of cards. */
const HeaderRow: React.FC<{ columns: string; widths: number[] }> = ({ columns, widths }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: columns, gap: 2, px: 3, py: 1, mb: 1 }}>
    {widths.map((width, index) => (
      <Box key={index}>{width > 0 && <Skeleton variant="text" width={width} />}</Box>
    ))}
  </Box>
);

/** Breadcrumbs, title, subtitle and buttons, as CreateSectionHeader lays them out. */
const HeaderSkeleton: React.FC<{ buttons?: number[]; badge?: boolean }> = ({
  buttons = [],
  badge = false,
}) => (
  <>
    <Box sx={{ mt: 1, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
      <Skeleton variant="circular" width={22} height={22} />
      <Skeleton variant="text" width={160} />
    </Box>
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 4 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Skeleton variant="text" width={220} sx={{ fontSize: "1.5rem" }} />
          {badge && <Chip width={56} />}
        </Box>
        <Skeleton variant="text" sx={{ maxWidth: 520 }} />
      </Box>
      {buttons.length > 0 && (
        <Box sx={{ display: { xs: "none", sm: "flex" }, gap: 1.5, flexShrink: 0 }}>
          {buttons.map((width, index) => (
            <Skeleton key={index} variant="rounded" width={width} height={40} sx={{ borderRadius: 2 }} />
          ))}
        </Box>
      )}
    </Box>
  </>
);

const SearchSkeleton: React.FC<{ sx?: object }> = ({ sx }) => (
  <Skeleton variant="rounded" height={48} sx={{ borderRadius: 2, ...sx }} />
);

/** The Planners tab while it loads: search, column headings, then Planner cards. */
export const PlannerListSkeleton: React.FC = () => {
  const isMobile = useIsMobile();
  const avatar = (
    <Skeleton
      variant="circular"
      width={MANAGEMENT_AVATAR_SIZE + 8}
      height={MANAGEMENT_AVATAR_SIZE + 8}
      sx={{ flexShrink: 0 }}
    />
  );

  return (
    <Box role="status" aria-label="Loading Planners">
      <SearchSkeleton sx={{ mb: 3 }} />
      {!isMobile && (
        <HeaderRow columns={PLANNER_LIST_COLUMNS} widths={[48, 64, 48, 80, 48, 88, 0]} />
      )}
      <Stack spacing={1.5}>
        {ROWS.map((row) =>
          isMobile ? (
            <Paper key={row} elevation={0} sx={{ ...CARD_SX, p: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {avatar}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="45%" sx={{ fontSize: "0.75rem" }} />
                  <Skeleton variant="text" width="35%" sx={{ fontSize: "0.75rem", mb: 0.75 }} />
                  <Chip />
                </Box>
              </Box>
            </Paper>
          ) : (
            <Paper
              key={row}
              elevation={0}
              sx={{
                ...CARD_SX,
                display: "grid",
                gridTemplateColumns: PLANNER_LIST_COLUMNS,
                gap: 2,
                px: 3,
                py: 2,
                alignItems: "center",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
                {avatar}
                <TwoLines first="75%" second="35%" />
              </Box>
              <Skeleton variant="text" width="60%" />
              <TwoLines first={20} second={40} />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                <Skeleton variant="circular" width={20} height={20} sx={{ flexShrink: 0 }} />
                <TwoLines first="55%" second="75%" />
              </Box>
              <Chip />
              <TwoLines first="75%" second="40%" />
              <Skeleton variant="circular" width={20} height={20} />
            </Paper>
          ),
        )}
      </Stack>
    </Box>
  );
};

/** The Upcoming tab's Events while they load: a month heading and its list of Events. */
export const UpcomingEventsSkeleton: React.FC = () => {
  const isMobile = useIsMobile();

  return (
    <Box role="status" aria-label="Loading Events">
      <Skeleton variant="text" width={120} sx={{ fontSize: "1.0625rem", mb: 1 }} />
      <Paper elevation={0} sx={{ ...CARD_SX, overflow: "hidden" }}>
        {ROWS.map((row) => (
          <Box
            key={row}
            sx={{
              borderTop: row === 0 ? "none" : "1px solid",
              borderColor: "divider",
              px: isMobile ? 2 : 3,
              py: 1.5,
              display: "grid",
              gridTemplateColumns: isMobile ? "minmax(0, 1fr) 24px" : UPCOMING_EVENT_COLUMNS,
              gap: 2,
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
              <Skeleton
                variant="circular"
                width={MANAGEMENT_AVATAR_SIZE}
                height={MANAGEMENT_AVATAR_SIZE}
                sx={{ flexShrink: 0 }}
              />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Skeleton variant="text" width="55%" />
                <Skeleton variant="rounded" width={96} height={20} sx={{ borderRadius: 4, mt: 0.5 }} />
              </Box>
            </Box>
            {!isMobile && (
              <>
                <IconLine />
                <IconLine width="60%" />
                <IconLine width="80%" />
              </>
            )}
            <Skeleton variant="circular" width={20} height={20} />
          </Box>
        ))}
      </Paper>
    </Box>
  );
};

/** A sent Planner's Events while they load: column headings, then Event cards. */
export const SentPlannerEventsSkeleton: React.FC = () => {
  const isMobile = useIsMobile();
  const avatar = (
    <Skeleton
      variant="circular"
      width={MANAGEMENT_AVATAR_SIZE}
      height={MANAGEMENT_AVATAR_SIZE}
      sx={{ flexShrink: 0 }}
    />
  );

  return (
    <Box role="status" aria-label="Loading Events">
      {!isMobile && (
        <HeaderRow columns={SENT_EVENT_COLUMNS} widths={[12, 48, 40, 40, 64, 48, 56]} />
      )}
      <Stack spacing={1.5}>
        {ROWS.map((row) =>
          isMobile ? (
            <Paper key={row} elevation={0} sx={{ ...CARD_SX, p: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {avatar}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="75%" sx={{ fontSize: "0.75rem", mb: 0.75 }} />
                  <Chip />
                </Box>
              </Box>
            </Paper>
          ) : (
            <Paper
              key={row}
              elevation={0}
              sx={{
                ...CARD_SX,
                display: "grid",
                gridTemplateColumns: SENT_EVENT_COLUMNS,
                gap: 2,
                px: 3,
                py: 1.5,
                alignItems: "center",
              }}
            >
              <Skeleton variant="text" width={12} />
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
                {avatar}
                <Skeleton variant="text" width="60%" />
              </Box>
              <IconLine />
              <IconLine width="60%" />
              <IconLine width="80%" />
              <Chip />
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                {[0, 1, 2].map((icon) => (
                  <Skeleton key={icon} variant="circular" width={28} height={28} />
                ))}
              </Box>
            </Paper>
          ),
        )}
      </Stack>
    </Box>
  );
};

/** A field in the Draft editor: its label, the box, and the hint under it. */
const FieldSkeleton: React.FC<{ height?: number }> = ({ height = 40 }) => (
  <Box>
    <Skeleton variant="text" width={110} sx={{ mb: 0.5 }} />
    <Skeleton variant="rounded" height={height} sx={{ borderRadius: 1 }} />
    <Skeleton variant="text" width="80%" sx={{ fontSize: "0.8125rem", mt: 0.75 }} />
  </Box>
);

/** The Draft editor's Events table: #, Title, dates and times, Location, Actions. */
const EVENTS_TABLE_COLUMNS = "40px minmax(0, 1fr) 200px 136px 200px 136px minmax(0, 1fr) 132px";

/** A Draft Planner while it loads: details panel, then the Events table. */
const DraftPlannerSkeleton: React.FC = () => {
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up("lg"));

  return (
    <>
      <HeaderSkeleton buttons={[120, 140]} badge />
      <Paper variant="outlined" sx={{ borderRadius: 2, p: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1.3fr" },
            gap: { xs: 3, md: 4 },
          }}
        >
          <FieldSkeleton height={56} />
          <FieldSkeleton height={52} />
          <FieldSkeleton height={52} />
        </Box>
      </Paper>
      <Paper variant="outlined" sx={{ borderRadius: 2, mt: 3, overflow: "hidden" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 2,
            px: { xs: 2, sm: 3 },
            pt: { xs: 2, sm: 3 },
            pb: 2,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width={80} sx={{ fontSize: "1.125rem" }} />
            <Skeleton variant="text" sx={{ maxWidth: 440, fontSize: "0.8125rem" }} />
          </Box>
          <Skeleton variant="rounded" width={120} height={40} sx={{ borderRadius: 2 }} />
        </Box>
        {wide ? (
          <Box sx={{ px: 2, pb: 2 }}>
            <Box sx={{ display: "grid", gridTemplateColumns: EVENTS_TABLE_COLUMNS, gap: 2, px: 1, py: 1 }}>
              {[12, 40, 72, 72, 64, 64, 64, 56].map((width, index) => (
                <Skeleton key={index} variant="text" width={width} />
              ))}
            </Box>
            {[0, 1, 2].map((row) => (
              <Box
                key={row}
                sx={{
                  display: "grid",
                  gridTemplateColumns: EVENTS_TABLE_COLUMNS,
                  gap: 2,
                  px: 1,
                  py: 1,
                  alignItems: "center",
                }}
              >
                <Skeleton variant="text" width={12} />
                {[0, 1, 2, 3, 4, 5].map((cell) => (
                  <Skeleton key={cell} variant="rounded" height={40} sx={{ borderRadius: 1 }} />
                ))}
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                  {[0, 1, 2].map((icon) => (
                    <Skeleton key={icon} variant="circular" width={28} height={28} />
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Stack spacing={2} sx={{ px: 2, pb: 2 }}>
            {[0, 1].map((row) => (
              <Skeleton key={row} variant="rounded" height={220} sx={{ borderRadius: 2 }} />
            ))}
          </Stack>
        )}
      </Paper>
    </>
  );
};

/** One fact in the sent Planner's summary panel: a round icon, label, value. */
const SummaryItemSkeleton: React.FC<{ hint?: boolean }> = ({ hint = false }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1, minWidth: 0 }}>
    <Skeleton variant="circular" width={52} height={52} sx={{ flexShrink: 0 }} />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Skeleton variant="text" width={72} sx={{ fontSize: "0.8125rem" }} />
      <Skeleton variant="text" width="50%" />
      {hint && <Skeleton variant="text" width="65%" sx={{ fontSize: "0.8125rem" }} />}
    </Box>
  </Box>
);

/** A sent Planner while it loads: summary panel, then its Events. */
const SentPlannerSkeleton: React.FC = () => (
  <>
    <HeaderSkeleton buttons={[110, 130, 40]} badge />
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 3,
        p: { xs: 2, sm: 3 },
        mb: 4,
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: { xs: 2, md: 4 },
      }}
    >
      <SummaryItemSkeleton hint />
      <SummaryItemSkeleton />
    </Paper>
    <Skeleton variant="text" width={64} />
    <Skeleton variant="text" sx={{ maxWidth: 420, fontSize: "0.8125rem", mb: 1.5 }} />
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
      <SearchSkeleton sx={{ flex: 1 }} />
      <Chip width={72} />
    </Box>
    <SentPlannerEventsSkeleton />
  </>
);

/**
 * A Planner page while it loads. Shaped like the Draft editor unless the
 * Planner is already known to be sent.
 */
export const PlannerSkeleton: React.FC<{ sent?: boolean }> = ({ sent = false }) => (
  <PageContainer sx={PAGE_SX}>
    <Box role="status" aria-label="Loading Planner">
      {sent ? <SentPlannerSkeleton /> : <DraftPlannerSkeleton />}
    </Box>
  </PageContainer>
);

/** The Planners page while its code loads: header, tabs, then the Planners tab. */
const PlannersSkeleton: React.FC = () => (
  <PageContainer sx={PAGE_SX}>
    <HeaderSkeleton buttons={[140]} />
    <Box sx={{ mb: 3, borderBottom: 1, borderColor: "divider", display: "flex", gap: 3, pb: 1.5 }}>
      <Skeleton variant="text" width={80} />
      <Skeleton variant="text" width={80} />
    </Box>
    <PlannerListSkeleton />
  </PageContainer>
);

export default PlannersSkeleton;
